import { useEffect, useMemo, useState } from 'react';
import { GILDAS } from '../data/gildas';
import { PRESETS } from '../data/presets';
import { waLink, stripAccents } from '../lib/contact';
import {
  toLocalISODate,
  parseLocalDate,
  getPickupSlots,
  fmtTime,
  dayLabel,
  HOURS,
} from '../lib/schedule';

const MIN_ORDER = 12;

/** Today + 2 days as YYYY-MM-DD in LOCAL timezone (no UTC drift). */
function defaultMinDate() {
  const t = new Date();
  t.setDate(t.getDate() + 2);
  return toLocalISODate(t);
}

/** Closed-day labels for hint text (e.g. "Martes (cerrado)"). */
const CLOSED_LABELS = HOURS.filter((h) => !h.ranges.length).map((h) => h.label);

export default function GildaOrderBuilder() {
  const [qty, setQty] = useState({});
  const [dateStr, setDateStr] = useState('');
  const [time, setTime] = useState('');
  const [name, setName] = useState('');

  const minDate = useMemo(defaultMinDate, []);
  const total = useMemo(
    () => Object.values(qty).reduce((a, b) => a + (b || 0), 0),
    [qty],
  );

  const dateObj = useMemo(() => parseLocalDate(dateStr), [dateStr]);
  const slots = useMemo(() => getPickupSlots(dateObj), [dateObj]);
  const dayClosed = !!dateObj && slots.length === 0;

  // Reset time if user picks a closed day or a day whose slots no longer include the chosen time.
  useEffect(() => {
    if (!dateObj) return;
    if (dayClosed) {
      if (time) setTime('');
      return;
    }
    if (time && !slots.includes(parseTime(time))) setTime('');
  }, [dateObj, dayClosed, slots, time]);

  // Listen for "add this gilda" events fired by external components (e.g. /pedido gallery tiles).
  useEffect(() => {
    const onAdd = (e) => {
      const { num } = e.detail || {};
      if (num) inc(num);
    };
    window.addEventListener('gilda:add', onAdd);
    return () => window.removeEventListener('gilda:add', onAdd);
  }, []);

  const inc = (num) => {
    setQty((p) => ({ ...p, [num]: (p[num] || 0) + 1 }));
    if (typeof document !== 'undefined') {
      const card = document.querySelector(`[data-testid="order-card-${num}"]`);
      if (card) {
        card.classList.remove('ob-card-pop');
        // Force reflow so the animation re-triggers on consecutive adds.
        void card.offsetWidth;
        card.classList.add('ob-card-pop');
      }
    }
  };
  const dec = (num) =>
    setQty((p) => ({ ...p, [num]: Math.max(0, (p[num] || 0) - 1) }));
  const reset = () => {
    setQty({});
    setDateStr('');
    setTime('');
    setName('');
  };
  const applyPreset = (preset) => {
    setQty({ ...preset.qty });
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame(() => {
        document.querySelector('[data-testid="ob-form-row"]')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  /** Identify if current qty matches a preset exactly. */
  const activePreset = useMemo(() => {
    return PRESETS.find((p) => {
      const keys = Object.keys(p.qty);
      const currentKeys = Object.entries(qty).filter(([, n]) => n > 0).map(([k]) => k);
      if (keys.length !== currentKeys.length) return false;
      return keys.every((k) => p.qty[k] === qty[k]);
    });
  }, [qty]);

  /**
   * Single-line ASCII-only message for the WhatsApp URL.
   * No newlines, no '¡ • × — –', no accents, no '!' (the api.whatsapp.com
   * parser can choke on '!' adjacent to other punctuation).
   */
  const message = useMemo(() => {
    const items = GILDAS
      .filter((g) => (qty[g.num] || 0) > 0)
      .map((g) => `${qty[g.num]} ${stripAccents(g.name)}`)
      .join(', ');

    const parts = ['Hola Bar Cabrero. Pedido de gildas:'];
    if (items) parts.push(items + '.');
    parts.push(`Total ${total}.`);
    if (dateObj && time) {
      parts.push(`Recogida ${stripAccents(dayLabel(dateObj).toLowerCase())} ${String(dateObj.getDate()).padStart(2,'0')}/${String(dateObj.getMonth()+1).padStart(2,'0')} a las ${time}.`);
    }
    if (name.trim()) parts.push(`A nombre de ${stripAccents(name.trim())}.`);
    parts.push('Gracias.');
    return parts.join(' ');
  }, [qty, total, dateObj, time, name]);

  const ready = total >= MIN_ORDER && !!dateObj && !dayClosed && !!time && !!name.trim();
  const remaining = Math.max(0, MIN_ORDER - total);
  const waUrl = ready ? waLink(message) : undefined;

  let hint;
  if (total === 0) hint = `Empieza añadiendo gildas. Mínimo ${MIN_ORDER}.`;
  else if (remaining > 0) hint = `Faltan ${remaining} para el mínimo (${MIN_ORDER}).`;
  else if (!dateObj) hint = 'Elige el día de recogida.';
  else if (dayClosed) hint = `Los ${dayLabel(dateObj).toLowerCase()} cerramos. Elige otro día.`;
  else if (!time) hint = 'Elige una hora dentro de nuestro horario.';
  else if (!name.trim()) hint = 'Falta tu nombre para el pedido.';
  else hint = 'Listo. Abrimos WhatsApp con el pedido pre-rellenado.';

  return (
    <section className="sec ob-section" data-testid="order-builder-section">
      {/* PRESETS — bandejas predefinidas */}
      <div className="ob-presets" data-testid="order-presets">
        <p className="ob-presets-label">Empieza con una bandeja predefinida</p>
        <div className="ob-presets-row">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => applyPreset(p)}
              className={`ob-preset ${activePreset?.id === p.id ? 'on' : ''}`}
              data-testid={`order-preset-${p.id}`}
            >
              <span className="ob-preset-name">{p.label}</span>
              <span className="ob-preset-meta">{p.total} gildas</span>
              <span className="ob-preset-desc">{p.desc}</span>
            </button>
          ))}
        </div>
        <p className="ob-presets-foot">
          O elige variedad por variedad abajo. Puedes ajustar cualquier preset después.
        </p>
      </div>

      <div className="ob-grid rv" data-testid="order-builder-grid">
        {GILDAS.map((g) => {
          const n = qty[g.num] || 0;
          return (
            <div
              key={g.num}
              className={`ob-card ${n > 0 ? 'ob-card-on' : ''}`}
              data-testid={`order-card-${g.num}`}
            >
              <div className="ob-card-top">
                <span className="ob-num">{g.num}</span>
                <span className="ob-name">{g.name}</span>
              </div>
              <p className="ob-desc">{g.desc}</p>
              <div className="ob-stepper" role="group" aria-label={`Cantidad ${g.name}`}>
                <button
                  type="button"
                  className="ob-btn"
                  onClick={() => dec(g.num)}
                  disabled={n === 0}
                  aria-label={`Quitar una ${g.name}`}
                  data-testid={`order-dec-${g.num}`}
                >
                  −
                </button>
                <span className="ob-qty" data-testid={`order-qty-${g.num}`}>
                  {n}
                </span>
                <button
                  type="button"
                  className="ob-btn ob-btn-plus"
                  onClick={() => inc(g.num)}
                  aria-label={`Añadir una ${g.name}`}
                  data-testid={`order-inc-${g.num}`}
                >
                  +
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="ob-form rv" data-testid="ob-form-row">
        <div className="ob-form-row">
          <label className="ob-label" htmlFor="ob-date">
            <span>Día de recogida</span>
            <input
              id="ob-date"
              type="date"
              min={minDate}
              value={dateStr}
              onChange={(e) => setDateStr(e.target.value)}
              className="ob-input"
              data-testid="order-date"
            />
            {dayClosed && (
              <span className="ob-error" data-testid="order-date-error">
                Los {dayLabel(dateObj).toLowerCase()} cerramos.
              </span>
            )}
          </label>

          <label className="ob-label" htmlFor="ob-time">
            <span>Hora de recogida</span>
            <select
              id="ob-time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              disabled={!dateObj || dayClosed}
              className="ob-input"
              data-testid="order-time"
            >
              <option value="">
                {!dateObj
                  ? 'Primero elige un día'
                  : dayClosed
                  ? 'Cerrado ese día'
                  : 'Elige una hora'}
              </option>
              {slots.map((m) => (
                <option key={m} value={fmtTime(m)}>
                  {fmtTime(m)}
                </option>
              ))}
            </select>
            {dateObj && !dayClosed && (
              <span className="ob-helper">Dentro de horario de apertura.</span>
            )}
          </label>

          <label className="ob-label" htmlFor="ob-name">
            <span>Tu nombre</span>
            <input
              id="ob-name"
              type="text"
              value={name}
              maxLength={60}
              placeholder="Ej. Marcos"
              onChange={(e) => setName(e.target.value)}
              className="ob-input"
              data-testid="order-name"
            />
          </label>
        </div>
        <p className="ob-closed-note">
          Cerrado los {CLOSED_LABELS.join(' y ').toLowerCase()}. Avísanos con dos días.
        </p>
      </div>

      <div className="ob-bar" role="status" data-testid="order-summary-bar">
        <div className="ob-bar-info">
          <span className="ob-total" data-testid="order-total">
            {total} <small>{total === 1 ? 'gilda' : 'gildas'}</small>
          </span>
          <span className="ob-hint" data-testid="order-hint">
            {hint}
          </span>
        </div>
        <div className="ob-bar-actions">
          <button
            type="button"
            className="btn btn-o"
            onClick={reset}
            disabled={total === 0 && !dateStr && !time && !name}
            data-testid="order-reset"
          >
            Empezar de cero
          </button>
          <a
            className={`btn btn-g ${!ready ? 'btn-disabled' : ''}`}
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-disabled={!ready}
            tabIndex={ready ? 0 : -1}
            onClick={(e) => { if (!ready) e.preventDefault(); }}
            data-testid="order-send-whatsapp"
          >
            Enviar por WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}

function parseTime(t) {
  if (!t) return -1;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}
